// Social wall: a row of tall reel-style tiles (Instagram look), scrolled with arrows or a swipe.
// Live mode: with "Instagram feed link (Behold)" set in theme settings, the latest Instagram posts
// (photos, videos, carousels) load automatically; a video plays inline on click, a photo opens the
// post on Instagram. Without a feed link the placeholder tiles below show and open the profile.
(function () {
  const { useEffect, useRef, useState } = React;
  const h = React.createElement;
  const BASE = window.ASSET_BASE || "";
  const S = window.THEME_SETTINGS || {};

  // Add real posts here: { video: "reel-1.mp4", poster: "reel-1.jpg", caption: "...", link: "https://www.instagram.com/reel/..." }
  const POSTS = [
    { caption: "Nap time, sorted", tint: "#f04923", video: "", poster: "", link: "" },
    { caption: "Sunday walk essentials", tint: "#9bb08a", video: "", poster: "", link: "" },
    { caption: "Two cats, one brush", tint: "#e8c07a", video: "", poster: "", link: "" },
    { caption: "Harness on, let's go", tint: "#7fb7c9", video: "", poster: "", link: "" },
    { caption: "Unboxing the treat ball", tint: "#d99ab0", video: "", poster: "", link: "" },
    { caption: "Grooming day glow-up", tint: "#b8a6e0", video: "", poster: "", link: "" },
    { caption: "Water break on the trail", tint: "#8fc0a9", video: "", poster: "", link: "" },
  ];
  const PAW_D = "M12 21c-2.2 0-4.6-1.3-4.6-3.6 0-2.4 2.3-4.9 4.6-4.9s4.6 2.5 4.6 4.9c0 2.3-2.4 3.6-4.6 3.6ZM5.2 12.6c-1.2.3-2.5-.7-2.9-2.3-.4-1.6.2-3.1 1.4-3.4 1.2-.3 2.5.7 2.9 2.3.4 1.6-.2 3.1-1.4 3.4Zm13.6 0c1.2.3 2.5-.7 2.9-2.3.4-1.6-.2-3.1-1.4-3.4-1.2-.3-2.5.7-2.9 2.3-.4 1.6.2 3.1 1.4 3.4ZM9.1 8.7c-1.3.1-2.4-1.3-2.5-3.1-.1-1.8.9-3.4 2.2-3.5 1.3-.1 2.4 1.3 2.5 3.1.1 1.8-.9 3.4-2.2 3.5Zm5.8 0c1.3.1 2.4-1.3 2.5-3.1.1-1.8-.9-3.4-2.2-3.5-1.3-.1-2.4 1.3-2.5 3.1-.1 1.8.9 3.4 2.2 3.5Z";

  const handle = (() => {
    const m = String(S.instagram || "").match(/instagram\.com\/([^/?#]+)/i);
    return m ? "@" + m[1] : "@the_pawtherapy";
  })();

  // Behold JSON feed -> tile posts. Accepts both the newer { posts: [...] } shape and a plain array.
  function fromFeed(json) {
    const list = Array.isArray(json) ? json : (json && json.posts) || [];
    // newest first (as Behold sends it); skip posts hidden in Behold; optional reels/videos-only; theme-set count
    const onlyVideo = S.instagramFilter === "video", max = Math.max(4, Math.min(12, Number(S.instagramCount) || 12));
    return list.filter((x) => x.visibility !== "hidden" && (!onlyVideo || x.mediaType === "VIDEO")).slice(0, max).map((x, i) => {
      const video = x.mediaType === "VIDEO";
      const sized = x.sizes && (x.sizes.medium || x.sizes.large || x.sizes.full);
      const still = (sized && sized.mediaUrl) || (video ? x.thumbnailUrl : x.mediaUrl) || x.thumbnailUrl || "";
      const text = String(x.prunedCaption || x.caption || "").replace(/\s+/g, " ").trim();
      return {
        caption: text.length > 70 ? text.slice(0, 67).trim() + "..." : text,
        tint: POSTS[i % POSTS.length].tint,
        remote: true, video: video ? x.mediaUrl || "" : "", poster: still, link: x.permalink || "",
        album: x.mediaType === "CAROUSEL_ALBUM",
      };
    }).filter((p) => p.poster);
  }

  function Tile({ p }) {
    const vid = useRef(null);
    const [playing, setPlaying] = useState(false);
    const go = () => {
      if (p.video && vid.current) {
        const v = vid.current;
        if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
        return;
      }
      const url = p.link || S.instagram;
      if (url) window.open(url, "_blank", "noopener");
    };
    return h("button", { type: "button", className: "sw-tile" + (playing ? " is-playing" : ""), style: { "--sw-tint": p.tint }, onClick: go, "aria-label": (p.video ? "Play: " : "Open on Instagram: ") + p.caption },
      p.video
        ? h("video", { ref: vid, className: "sw-media", src: (p.remote ? "" : BASE) + p.video, poster: p.poster ? (p.remote ? "" : BASE) + p.poster : undefined, playsInline: true, loop: true, preload: "none", onEnded: () => setPlaying(false) })
        : p.poster
          ? h("img", { className: "sw-media", src: (p.remote ? "" : BASE) + p.poster, alt: p.caption || "Instagram post", loading: "lazy" })
          : h("span", { className: "sw-ph", "aria-hidden": "true" }, h("svg", { viewBox: "0 0 24 24", focusable: "false" }, h("path", { d: PAW_D }))),
      (p.video || !p.remote) && h("span", { className: "sw-play", "aria-hidden": "true" }, h("svg", { viewBox: "0 0 24 24", focusable: "false" }, h("path", { d: "M8 5.5v13l11-6.5z" }))),
      p.album && h("span", { className: "sw-album", "aria-hidden": "true" }, h("svg", { viewBox: "0 0 24 24", focusable: "false" }, h("path", { d: "M7 3h12a2 2 0 0 1 2 2v12h-2V5H7zM3 7h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" }))),
      h("span", { className: "sw-cap" }, h("span", { className: "sw-handle" }, handle), p.caption || null));
  }

  function PtSocialWall() {
    const track = useRef(null);
    const [edge, setEdge] = useState({ start: true, end: false });
    const [posts, setPosts] = useState(POSTS);
    useEffect(() => {
      if (!S.instagramFeed) return;
      let live = true;
      fetch(S.instagramFeed).then((r) => (r.ok ? r.json() : null)).then((j) => {
        const got = j ? fromFeed(j) : [];
        if (live && got.length) setPosts(got);
      }).catch(() => {});                                   // feed down: keep the placeholders
      return () => { live = false; };
    }, []);
    useEffect(() => { update(); }, [posts]);
    const update = () => {
      const t = track.current; if (!t) return;
      setEdge({ start: t.scrollLeft < 8, end: t.scrollLeft + t.clientWidth > t.scrollWidth - 8 });
    };
    useEffect(() => {
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }, []);
    const move = (dir) => {
      const t = track.current; if (!t) return;
      const tile = t.querySelector(".sw-tile");
      const step = tile ? tile.offsetWidth + 16 : t.clientWidth * 0.8;
      t.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    return h("section", { className: "section sw" + (posts !== POSTS ? " is-live" : ""), "aria-label": "From our Instagram" },
      h("div", { className: "section-head" },
        h("h2", { className: "section-title" }, "social media ", h("em", null, "wall")),
        S.instagram && h("a", { className: "link-arrow", href: S.instagram, target: "_blank", rel: "noopener" }, "Follow " + handle + " \u2197")),
      h("div", { className: "sw-wrap" },
        h("button", { type: "button", className: "sw-arrow sw-prev", onClick: () => move(-1), disabled: edge.start, "aria-label": "Previous posts" }, "\u2039"),
        h("div", { ref: track, className: "sw-track", onScroll: update },
          posts.map((p, i) => h(Tile, { key: (p.link || "") + i, p }))),
        h("button", { type: "button", className: "sw-arrow sw-next", onClick: () => move(1), disabled: edge.end, "aria-label": "Next posts" }, "\u203a")));
  }

  window.PtSocialWall = PtSocialWall;
})();
